import { useState } from 'react';

import { motion } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { CancelSaveButtons } from '@components/cancel-save-buttons/cancel-save-buttons';
import { Card } from '@components/card';
import { CopyButton } from '@components/copy-button';
import { FileButton } from '@components/file-button';
import { createForm, useCreateFormStore } from '@components/form';
import { IconButton } from '@components/icon-button';
import { IconAzure } from '@components/icons/azure';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCompany } from '@components/icons/company';
import { IconEdit } from '@components/icons/edit';
import { IconEmailAddress } from '@components/icons/email-address';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconPassword } from '@components/icons/password';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { IconUpload } from '@components/icons/upload';
import { IconUrl } from '@components/icons/url';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { InfoBox } from '@components/info-box';
import { LabelTextGroup } from '@components/label-text-group';
import { RelativeSpinner } from '@components/loading-spinner';
import { PageContainers } from '@components/page-containers';
import { PageContent } from '@components/page-containers/page-container';
import { PasswordStrengthBar } from '@components/password-strength-bar';
import { PremiumTag } from '@components/premium-tag';
import { ImageViewer } from '@components/previewer';
import { RadioGroup } from '@components/radio-group';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { showSuccess } from '@components/toaster';
import { VisuallyHidden } from '@components/visually-hidden';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import {
  DEFAULT_COMPANY_LOGO_DESKTOP_URL,
  DEFAULT_COMPANY_LOGO_MOBILE_URL,
  DEFAULT_PORTAL_BANNER_URL,
} from '@utils/assets';
import { pxToRem } from '@utils/converters';

import {
  useEditPortalDomain,
  usePortalDomain,
  usePortalSecurity,
  useUpdatePortal,
  useUpdatePortalSecurity,
} from 'api/portals/hooks';
import { PortalDetail, PortalSecuritySettings, SecurityType } from 'api/portals/requests';
import { useFeature } from 'ui/hooks';

const DNS_A_ADDRESS = '37.16.7.207';

export const PortalSettings = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <PageContent>
      <PageContainers.MiddleColumnPage>
        <Stack space={4}>
          <Stack space={2}>
            <Text font="heading2" color="textPrimary" text={t('portalConfiguration')} />
            <Text font="regular14" color="textSecondary" text={t('portalHelptext')} />
          </Stack>
          <Card space={6} variant="transparent">
            <PortalNameSettings portal={portal} />
          </Card>
          <Card space={6} variant="transparent">
            <PortalDomainSettings portal={portal} />
          </Card>
        </Stack>
        <Separator margin="1rem 0" />
        <PortalImagesSection portal={portal} />
      </PageContainers.MiddleColumnPage>
    </PageContent>
  );
};

export const PortalSecurity = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <PageContent>
      <PageContainers.MiddleColumnPage>
        <Stack space={4}>
          <Stack space={2}>
            <Text font="heading2" color="textPrimary" text={t('securitySettings')} />
            <Text font="regular14" color="textSecondary" text={t('securityHelpText')} />
          </Stack>
          <Card space={6} variant="transparent">
            <PortalSecurityTypeSelector portal={portal} />
          </Card>
        </Stack>
      </PageContainers.MiddleColumnPage>
    </PageContent>
  );
};

const PortalSecurityTypeSelector = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  const { data: securitySettings, isLoading } = usePortalSecurity(portal.shortId);
  const updateSecurityMutation = useUpdatePortalSecurity(portal.shortId);

  const handleSecurityTypeChange = async (securityType: SecurityType) => {
    await updateSecurityMutation.mutateAsync({ securityType });
  };

  if (isLoading || !securitySettings) {
    return <RelativeSpinner />;
  }

  return (
    <Stack space={6}>
      <RadioGroup
        defaultValue={securitySettings.securityType}
        label={t('securityMethod')}
        hideLabel
        onChange={handleSecurityTypeChange}
      >
        <RadioGroup.Option label={t('none')} value="none" icon={<IconUrl color="purple50" />} />
        <RadioGroup.Option
          label={t('passwordProtection')}
          value="password"
          icon={<IconPassword color="purple50" />}
        />
        <RadioGroup.Option
          label={t('azureSSOLogin')}
          value="azure_sso"
          icon={<IconAzure color="purple50" />}
        />
      </RadioGroup>

      {securitySettings.securityType === 'password' && (
        <PortalPasswordSettings portal={portal} securitySettings={securitySettings} />
      )}
      {securitySettings.securityType === 'azure_sso' && (
        <PortalAzureSSOSettings portal={portal} securitySettings={securitySettings} />
      )}
    </Stack>
  );
};

type AzureSSOType = {
  azureTenantId: string;
  azureClientId: string;
  azureAuthorizedGroupId: string;
};

const AzureSSOForm = createForm<AzureSSOType>();

const PortalAzureSSOSettings = ({
  portal,
  securitySettings,
}: {
  portal: PortalDetail;
  securitySettings: PortalSecuritySettings;
}) => {
  const { t } = useScopedTranslate(locale);
  const updateSecurityMutation = useUpdatePortalSecurity(portal.shortId);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();

  const handleSave = async (data: AzureSSOType) => {
    return updateSecurityMutation.mutateAsync(data);
  };

  return (
    <Stack space={4}>
      <LabelTextGroup
        label={t('azureSSOConfiguration')}
        text={t('azureSSOConfigurationHelpText')}
      />
      <InfoBox variant="indigo">
        <Stack orientation="horizontal" align="top" space={2}>
          <IconFormIndicatorInfo color="indigo100" />
          <Text
            font="regular14"
            text={styledTranslate({
              key: 'azureSetupInstructions',
              variables: {
                here: {
                  text: (
                    <a
                      href="https://help.journeys.50skills.app/en/articles/460689-portal-sso-setup-help-article"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'underline' }}
                    >
                      {t('here')}
                    </a>
                  ),
                },
              },
              translations: locale,
            })}
          />
        </Stack>
      </InfoBox>

      <AzureSSOForm store={formStore} onSubmit={handleSave}>
        <Stack space={4}>
          <AzureSSOForm.TextField
            id="azureTenantId"
            label={t('azureTenantId')}
            defaultValue={securitySettings.azureTenantId ?? ''}
            placeholder={t('azureTenantIdPlaceholder')}
            helpText={t('azureTenantIdHelpText')}
          />
          <AzureSSOForm.TextField
            id="azureClientId"
            label={t('azureClientId')}
            defaultValue={securitySettings.azureClientId}
            placeholder={t('azureClientIdPlaceholder')}
            helpText={t('azureClientIdHelpText')}
          />
          <AzureSSOForm.TextField
            id="azureAuthorizedGroupId"
            label={t('azureAuthorizedGroupId')}
            defaultValue={securitySettings.azureAuthorizedGroupId ?? ''}
            placeholder={t('azureAuthorizedGroupIdPlaceholder')}
            helpText={t('azureAuthorizedGroupIdHelpText')}
          />
          {isDirty && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <AzureSSOForm.Actions>
                <AzureSSOForm.Reset text={t('core.cancel')} />
                <AzureSSOForm.Submit text={t('core.save')} variant="primary-blue" />
              </AzureSSOForm.Actions>
            </motion.div>
          )}
        </Stack>
      </AzureSSOForm>
    </Stack>
  );
};

type DomainOption = 'fiftyDomain' | 'customDomain';

type PortalNameType = {
  name: string;
};

const PortalNameForm = createForm<PortalNameType>();

const PortalNameSettings = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  const submitMutation = useUpdatePortal(portal.shortId);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();

  const handleSave = async (data: PortalNameType) => {
    return submitMutation.mutateAsync(data);
  };

  return (
    <PortalNameForm store={formStore} onSubmit={handleSave}>
      <Text font="bold14" color="textPrimary" text={t('portalName')} />
      <PortalNameForm.TextField
        id="name"
        label={t('portalName')}
        hideLabel
        defaultValue={portal.name}
      />
      {isDirty && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <PortalNameForm.Actions>
            <PortalNameForm.Reset text={t('core.cancel')} />
            <PortalNameForm.Submit text={t('core.save')} variant="primary-blue" />
          </PortalNameForm.Actions>
        </motion.div>
      )}
    </PortalNameForm>
  );
};

const PortalFaviconSettings = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  const [faviconDraft, setFaviconDraft] = useState<File | null>(null);
  const submitMutation = useUpdatePortal(portal.shortId, {
    onSuccessText: t('portalUpdated'),
    onSuccess: () => {
      setFaviconDraft(null);
    },
  });

  const submitFavicon = async () => {
    const data = new FormData();
    if (faviconDraft) {
      data.append('favicon', faviconDraft);
    }
    await submitMutation.mutateAsync(data);
  };

  const favicon = portal.favicon ?? DEFAULT_COMPANY_LOGO_MOBILE_URL;

  return (
    <Stack orientation="vertical" space={4}>
      <Text font="bold14" color="textPrimary" text={t('favicon')} />
      <Stack orientation="horizontal" justify="space-between">
        <ImageViewer
          url={faviconDraft ? URL.createObjectURL(faviconDraft) : favicon}
          maxHeight={40}
        />
        {Boolean(faviconDraft) ? (
          <CancelSaveButtons
            isOpen={Boolean(faviconDraft)}
            onCancel={() => setFaviconDraft(null)}
            onSave={submitFavicon}
            isDisabled={submitMutation.isLoading}
          />
        ) : (
          <FileButton
            isReplace
            replaceText={t('replaceFavicon')}
            onFileSelected={setFaviconDraft}
            fileType="image"
          />
        )}
      </Stack>

      <Stack space={2}>
        <div>
          <Text font="regular14" color="textSecondary" text={t('faviconHelpText')} />
          <Text
            font="regular14"
            color="textHelper"
            text={t('acceptedFiles', { files: 'PNG & SVG' })}
          />
        </div>
      </Stack>
    </Stack>
  );
};

const PortalDomainSettings = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  const { isEnabled: hasCustomPortalDomain } = useFeature('FEATURE_CUSTOM_PORTAL_DOMAIN');
  const [selectedPortalDomainOption, setSelectedPortalDomainOption] = useState<DomainOption>(
    portal.hasCustomDomain ? 'customDomain' : 'fiftyDomain'
  );
  return (
    <Stack space={2}>
      <Text font="semibold14" text={t('domain')} />
      <RadioGroup
        defaultValue={selectedPortalDomainOption}
        label={t('portalDomain')}
        hideLabel
        onChange={(value: DomainOption) => setSelectedPortalDomainOption(value)}
      >
        <RadioGroup.Option
          label={t('portalDomainUseFifty')}
          value="fiftyDomain"
          icon={<IconEmailAddress color="purple50" />}
        />
        <RadioGroup.Option
          label={t('portalDomainUseCustom')}
          value="customDomain"
          icon={<IconCompany color="purple50" />}
          isDisabled={!hasCustomPortalDomain}
          disabledTag={hasCustomPortalDomain ? undefined : <PremiumTag />}
        />
      </RadioGroup>
      {selectedPortalDomainOption === 'fiftyDomain' ? (
        <PortalPath portal={portal} />
      ) : (
        <PortalDomain portal={portal} />
      )}
    </Stack>
  );
};

type PortalPasswordType = {
  newPassword: string;
  invalidateAllSessions: boolean;
};

const PortalPasswordForm = createForm<PortalPasswordType>();

const PortalPasswordSettings = ({
  portal,
  securitySettings,
}: {
  portal: PortalDetail;
  securitySettings: PortalSecuritySettings;
}) => {
  const { t } = useScopedTranslate(locale);
  const updateSecurityMutation = useUpdatePortalSecurity(portal.shortId);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const formStore = useCreateFormStore();
  const { setErrors } = formStore.actions;
  const isDirty = formStore.useIsDirty();
  const formValues = formStore.useFormValues();
  const password = (formValues['newPassword'] as string) ?? '';
  const hadPassword = Boolean(securitySettings.password);

  const displayedPassword = isPasswordVisible
    ? securitySettings.password
    : '•'.repeat(securitySettings.password.length);

  const submitPassword = async (data: PortalPasswordType) => {
    if (!data.newPassword) {
      setErrors({ newPassword: t('passwordIsRequired') });
      return;
    }
    await updateSecurityMutation.mutateAsync(data, {
      onSuccess: () => {
        showSuccess(t('passwordSet'));
      },
    });
    setIsEditing(false);
  };

  return (
    <Stack space={4}>
      <LabelTextGroup label={t('passwordProtection')} text={t('passwordProtectionHelpText')} />
      <Stack orientation="horizontal" space={2} justify="space-between" align="flex-end">
        {isEditing ? (
          <PasswordEditContainer>
            <PortalPasswordForm store={formStore} onSubmit={submitPassword}>
              <Stack space={4}>
                <Stack space={2} style={{ width: '100%' }}>
                  <HiddenUsernameField companySlug={portal.companySlug} portalSlug={portal.slug} />
                  <PortalPasswordForm.PasswordField
                    id="newPassword"
                    label={t('core.password')}
                    defaultValue={securitySettings.password}
                    autoComplete="new-password"
                    autoFocus
                  />
                  <PasswordStrengthBar password={password} />
                </Stack>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <Stack
                    orientation="horizontal"
                    justify={hadPassword ? 'space-between' : 'flex-end'}
                  >
                    {hadPassword && (
                      <PortalPasswordForm.Checkbox
                        id="invalidateAllSessions"
                        label={t('invalidateAllSessions')}
                        defaultValue={false}
                      />
                    )}
                    <PortalPasswordForm.Actions>
                      <PortalPasswordForm.Reset
                        text={t('core.cancel')}
                        onClick={() => setIsEditing(false)}
                      />
                      <PortalPasswordForm.Submit
                        iconLeft={<IconCheckmark color="buttonIconNegative" />}
                        text={t('core.save')}
                        variant="primary-blue"
                        isDisabled={!isDirty}
                      />
                    </PortalPasswordForm.Actions>
                  </Stack>
                </motion.div>
              </Stack>
            </PortalPasswordForm>
          </PasswordEditContainer>
        ) : (
          <>
            <LabelTextGroup label={t('core.password')} text={displayedPassword} />
            <Stack orientation="horizontal" space={2}>
              <IconButton
                icon={
                  isPasswordVisible ? (
                    <IconVisibilityShown color="buttonIconPositive" />
                  ) : (
                    <IconVisibilityHidden color="buttonIconPositive" />
                  )
                }
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              />
              <CopyButton getText={() => securitySettings.password} hasToast />
              <Button
                text={t('core.edit')}
                iconLeft={<IconEdit color="buttonIconPositive" />}
                variant="secondary"
                onClick={() => setIsEditing(true)}
              />
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
};

const PasswordEditContainer = styled.div({
  width: '100%',
});

type PortalFormType = {
  slug: string;
};

const SlugForm = createForm<PortalFormType>();

const PortalPath = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const submitMutation = useUpdatePortal(portal.shortId);
  const isDirty = formStore.useIsDirty();
  const { setErrors } = formStore.actions;

  const handleSave = async (data: PortalFormType) => {
    return submitMutation.mutateAsync(
      { ...data, hasCustomDomain: false },
      {
        onError: (e: any) => {
          const errorMessage = e?.response?.data?.slug?.[0] as string;
          setErrors({ slug: errorMessage });
        },
      }
    );
  };

  return (
    <SlugForm store={formStore} onSubmit={(data) => handleSave(data)}>
      <SlugForm.PrefixedTextField
        id="slug"
        label={t('companyPortalPath')}
        defaultValue={portal.slug}
        prefix={`portal.50skills.app/${portal.companySlug}/`}
      />

      {(isDirty || portal.hasCustomDomain) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <SlugForm.Actions>
            <SlugForm.Reset text={t('core.cancel')} />
            <SlugForm.Submit text={t('core.save')} variant="primary-blue" />
          </SlugForm.Actions>
        </motion.div>
      )}
    </SlugForm>
  );
};

type PortalDomainType = {
  domain: string;
};

const PortalDomainForm = createForm<PortalDomainType>();

const PortalDomain = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const submitMutation = useEditPortalDomain(portal.shortId);
  const { isLoading, data } = usePortalDomain(portal.shortId);

  const isDirty = formStore.useIsDirty();

  const InfoBoxContent = (text: string) => {
    return (
      <InfoBox variant="indigo">
        <Stack orientation="horizontal" align="top" space={2}>
          <IconFormIndicatorInfo color="indigo100" />
          <Text font="regular14" text={text} />
        </Stack>
      </InfoBox>
    );
  };
  const DNSCopy = (
    <Stack orientation="horizontal" justify="space-between" align="center">
      <div>
        <Text font="semibold14" text={t('dnsRecord')} />
        <Text font="regular14" color="textHelper" text={DNS_A_ADDRESS} />
      </div>
      <CopyButton getText={() => DNS_A_ADDRESS} copyLabel={t('copy')} />
    </Stack>
  );

  const handleSave = async (data: PortalDomainType) => {
    return submitMutation.mutateAsync(data);
  };

  if (isLoading || !data) {
    return <RelativeSpinner />;
  }

  const Info = () => {
    switch (data.status) {
      case 'ACTIVE_SSL':
        return null;
      case 'ACTIVE_SSL_PROXIED':
        return InfoBoxContent(t('sslProxiedInstructions'));
      case 'ACTIVE_NO_SSL':
        return InfoBoxContent(t('activeNoSSLInstructions'));
      case 'TARGET_NOT_LOADING':
        return InfoBoxContent(t('targetNotLoadingInstructions'));
      case 'DNS_INCORRECT':
        return (
          <>
            {DNSCopy}
            {InfoBoxContent(t('dnsIncorrectInstructions', { dns: DNS_A_ADDRESS }))}
          </>
        );
      case 'DNS_NOT_RESOLVING':
        return (
          <>
            {DNSCopy}
            {InfoBoxContent(t('dnsInstructions', { dns: DNS_A_ADDRESS }))}
          </>
        );
      case 'UNKNOWN':
        return (
          <>
            {DNSCopy}
            {InfoBoxContent(t('unknownInstructions'))}
          </>
        );
      default:
        return InfoBoxContent(t('initialInstructions'));
    }
  };

  return (
    <PortalDomainForm store={formStore} onSubmit={handleSave}>
      <PortalDomainForm.PrefixedTextField
        id="domain"
        label={t('portalDomain')}
        defaultValue={data.domain}
        prefix="https://"
      />
      <Info />
      {(isDirty || !portal.hasCustomDomain) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <PortalDomainForm.Actions>
            <PortalDomainForm.Reset text={t('core.cancel')} />
            <PortalDomainForm.Submit text={t('core.save')} variant="primary-blue" />
          </PortalDomainForm.Actions>
        </motion.div>
      )}
    </PortalDomainForm>
  );
};

type PortalLogoOption = 'companyLogo' | 'portalLogo';

const PortalImagesSection = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);
  const submitMutation = useUpdatePortal(portal.shortId, {
    onSuccessText: t('portalUpdated'),
    onSuccess: () => {
      setPortalLogoDraft(null);
      setBannerDraft(null);
    },
  });
  const [portalLogoDraft, setPortalLogoDraft] = useState<File | null>(null);
  const [bannerDraft, setBannerDraft] = useState<File | null>(null);
  const [selectedRadioOption, setSelectedRadioOption] = useState<PortalLogoOption>(
    portal.logo ? 'portalLogo' : 'companyLogo'
  );

  const handleOnRadioGroupChange = (value: PortalLogoOption) => {
    if (value === 'companyLogo') {
      const data = new FormData();
      data.append('logo', new File([], ''));
      submitMutation.mutateAsync(data);
    }
    setSelectedRadioOption(value);
  };

  const submitPortalLogo = async () => {
    const data = new FormData();
    if (portalLogoDraft) {
      data.append('logo', portalLogoDraft);
    }
    await submitMutation.mutateAsync(data);
  };

  const submitPortalBanner = async () => {
    const data = new FormData();
    if (bannerDraft) {
      data.append('banner', bannerDraft);
    }
    await submitMutation.mutateAsync(data);
  };

  const portalLogo = portal.logo ?? DEFAULT_COMPANY_LOGO_DESKTOP_URL;
  const companyPortalBanner = portal.banner ?? DEFAULT_PORTAL_BANNER_URL;

  return (
    <Stack space={6}>
      <Stack space={2}>
        <Text font="heading2" color="textPrimary" text={t('portalBranding')} />
        <Text font="regular14" color="textSecondary" text={t('portalBrandinghelpText')} />
      </Stack>
      <Card space={6} variant="transparent">
        <PortalFaviconSettings portal={portal} />
      </Card>
      <Card space={6} variant="transparent">
        <Stack space={4}>
          <Text font="bold14" color="textPrimary" text={t('portalLogo')} />
          <RadioGroup
            defaultValue={selectedRadioOption}
            label={t('portalLogo')}
            hideLabel
            onChange={handleOnRadioGroupChange}
          >
            <RadioGroup.Option
              label={t('useDefaultLogo')}
              value="companyLogo"
              icon={<IconTextFormatLink color="purple50" />}
            />
            <RadioGroup.Option
              label={t('addCustom')}
              value="portalLogo"
              icon={<IconUpload color="purple50" />}
            />
          </RadioGroup>
          {selectedRadioOption === 'portalLogo' && (
            <>
              <Stack orientation="horizontal" justify="space-between">
                <ImageViewer
                  url={portalLogoDraft ? URL.createObjectURL(portalLogoDraft) : portalLogo}
                  maxHeight={40}
                />
                {Boolean(portalLogoDraft) ? (
                  <CancelSaveButtons
                    isOpen={Boolean(portalLogoDraft)}
                    onCancel={() => setPortalLogoDraft(null)}
                    onSave={submitPortalLogo}
                    isDisabled={submitMutation.isLoading}
                  />
                ) : (
                  <FileButton
                    isReplace
                    replaceText={t('replaceLogo')}
                    onFileSelected={setPortalLogoDraft}
                    fileType="image"
                  />
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Card>
      <Card space={4} variant="transparent">
        <Stack orientation="vertical" space={4}>
          <Text font="bold14" color="textPrimary" text={t('portalBanner')} />
          <Text font="regular14" color="textSecondary" text={t('portalBannerHelpText')} />
          <ImageElementBanner
            alt="portal banner"
            src={bannerDraft ? URL.createObjectURL(bannerDraft) : companyPortalBanner}
          />
          <Text font="regular14" color="textSecondary" text={t('recommendedSize')} />
          <ButtonGroup align="right">
            {Boolean(bannerDraft) ? (
              <CancelSaveButtons
                isOpen={Boolean(bannerDraft)}
                onCancel={() => setBannerDraft(null)}
                onSave={submitPortalBanner}
                isDisabled={submitMutation.isLoading}
              />
            ) : (
              <FileButton
                isReplace
                replaceText={t('replaceBanner')}
                onFileSelected={setBannerDraft}
                fileType="image"
              />
            )}
          </ButtonGroup>
        </Stack>
      </Card>
    </Stack>
  );
};

const HiddenUsernameField = ({
  companySlug,
  portalSlug,
}: {
  companySlug: string;
  portalSlug: string;
}) => {
  return (
    <VisuallyHidden>
      <input
        type="text"
        name="username"
        autoComplete="username"
        value={`${companySlug}-${portalSlug}`}
        readOnly
      />
    </VisuallyHidden>
  );
};

const ImageElementBanner = styled.img({
  width: '100%',
  height: 'auto',
  maxHeight: pxToRem(96),
  objectFit: 'cover',
  display: 'block',
});

export const locale = {
  en: {
    portalConfiguration: 'Portal Configuration',
    portalHelptext:
      "Keep your external forms easily accessible for anyone in just a few clicks. Set up and configure your company's portal",
    companyPortalPath: 'Portal path',
    portalBranding: 'Custom portal branding',
    portalBrandinghelpText: "Configure your portal's appearance",
    portalLogo: 'Portal logo',
    useDefaultLogo: 'Use default company logo',
    addCustom: 'Add custom portal logo',
    portalBanner: 'Portal banner image',
    portalBannerHelpText: 'Add the image you want displayed in the header of this portal',
    recommendedSize: 'Recommended size is 2880 x 448 pixels',
    replaceLogo: 'Replace logo',
    portalUpdated: 'Portal updated',
    portalDomain: 'Custom portal domain',
    dnsRecord: 'DNS record',
    initialInstructions:
      'When adding a custom domain we must first create a virtual host for your domain. Hit save for instructions on configuring your DNS records',
    dnsInstructions:
      'Please add a DNS A record pointing to {dns}. If an A record already exists, update it and remove any other A, AAAA, or CNAME records for this domain.\n\nNote that it may take a few minutes for the SSL certificate to take effect after the DNS A record is updated',
    dnsIncorrectInstructions:
      'The DNS is pointing elsewhere. Update the A record to {dns}.\n\nNote that it may take a few minutes for the SSL certificate to take effect after the DNS A record is updated',
    activeNoSSLInstructions:
      "DNS is correct but the SSL hasn't been issued yet. This is usually brief—no action needed.",
    targetNotLoadingInstructions:
      "DNS is correct and traffic is reaching us, but your application isn't responding. Please check that it's running.",
    sslProxiedInstructions:
      'Traffic is reaching us through a proxy. SSL is active and there are no known issues.',
    unknownInstructions:
      "Status is currently unknown. This is usually temporary, we'll keep monitoring.",
    portalDomainUseFifty: 'Public domain',
    portalDomainUseCustom: 'Custom domain',
    copyLink: 'Copy link',
    replaceBanner: 'Replace banner',
    portalName: 'Portal name',
    domain: 'Domain',
    favicon: 'Favicon',
    acceptedFiles: 'Accepted file types: {files}',
    replaceFavicon: 'Replace favicon',
    faviconHelpText: 'Recommended size is 16 x 16 or 32 x 32 pixels',
    slugIsAlreadyInUse: 'A portal with this domain already exists in your company.',
    passwordProtection: 'Password protection',
    passwordProtectionHelpText: 'Protect your portal with a password to access the content',
    requirePassword: 'Require password',
    publicPortal: 'Portal is publicly accessible',
    privatePortal: 'Portal is password protected',
    passwordSet: 'Password set',
    invalidateAllSessions: 'Should all current users be logged out?',
    passwordIsRequired: 'Password is required',
    copy: 'Copy',
    securitySettings: 'Security Settings',
    securityHelpText:
      'Manage your portal security settings and make sure your Portal is secure, you can either set a password or use Microsoft Azure (SSO) login',
    selectSecurityMethod: 'Select your security method',
    securityMethod: 'Security method',
    none: 'None (open to anyone with the link)',
    noneDescription: 'Anyone with the portal link can access the content',
    passwordProtectionRadioDescription: 'Users need to enter a password to access the portal',
    azureSSOLogin: 'Azure (SSO) Login',
    azureSSODescription: 'Users authenticate using their Azure Active Directory credentials',
    azureSSOConfiguration: 'Azure SSO Configuration',
    azureSSOConfigurationHelpText: 'Configure your Azure Active Directory settings for SSO',
    azureSetupInstructions:
      "Azure setup instructions can be found {here}. After configuring on the Azure end, add the Tenant and Client ID's here below",
    here: 'here',
    azureTenantId: 'Azure Tenant ID *',
    azureTenantIdPlaceholder: 'Enter your Azure Tenant ID',
    azureTenantIdHelpText:
      'Enter your Azure AD Directory (Tenant) ID - a GUID that uniquely identifies your organizations Azure Active Directory',
    azureClientId: 'Azure Client ID (Application ID) *',
    azureClientIdPlaceholder: 'Enter your Azure Client ID',
    azureClientIdHelpText: 'Unique ID of your Azure AD application',
    azureAuthorizedGroupId: 'Authorized Group ID (Optional)',
    azureAuthorizedGroupIdPlaceholder: 'Enter Azure AD Group Object ID',
    azureAuthorizedGroupIdHelpText:
      'Optional. Leave empty to allow all Azure users in your organization, or specify an Azure AD Group Object ID to restrict access to only members of that group. Find the Object ID in Azure Portal > Azure Active Directory > Groups',
  },
};
